import { Link } from 'react-router-dom';
import { 
  Building2, 
  FileText, 
  Scale, 
  Shield, 
  CheckCircle2,
  Globe,
  GraduationCap
} from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';

const About = () => {
  const governanceFeatures = [
    {
      icon: Building2,
      title: 'Parent Company Oversight',
      description: 'Formal subsidiary of DAAS Limited with defined reporting structures.',
    },
    {
      icon: FileText,
      title: 'SOP-Driven Operations',
      description: 'Documented Standard Operating Procedures ensure consistency.',
    },
    {
      icon: Scale,
      title: 'Ethical Framework',
      description: 'Formal guidelines govern all recommendations and decisions.',
    },
    {
      icon: Shield,
      title: 'Compliance Standards',
      description: 'Adherence to internal policies and regulatory requirements.',
    },
  ];

  return (
    <main data-testid="about-page">
      {/* Page Header */}
      <section className="page-header" data-testid="about-header">
        <div className="container-main">
          <div className="breadcrumb">
            <Link to="/" data-testid="breadcrumb-home">Our Services</Link>
            <span>/</span>
            <span className="text-white">About</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4 opacity-0 animate-fade-in-up">
            About DAAS Global
          </h1>
        </div>
      </section>

      {/* Who We Are */}
      <section className="section-padding bg-white" data-testid="who-we-are-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <ScrollReveal>
              <div>
                <p className="text-[var(--secondary)] text-xs font-bold uppercase tracking-[0.2em] mb-4">
                  Who We Are
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--primary)] tracking-tight mb-6">
                  A Disciplined Approach to Education Consultancy
                </h2>
                <div className="gold-accent-line mb-8" />
              </div>
            </ScrollReveal>
            <ScrollReveal delay={150}>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">
                DAAS Global is an international student education consultancy operating as a subsidiary of DAAS Limited. 
                Unlike typical agencies focused on volume and sales, we prioritize proper guidance and realistic expectations 
                built on firsthand international education exposure.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Managing Director Section */}
      <section className="section-padding bg-[var(--background)]" data-testid="md-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <ScrollReveal>
              <div className="aspect-square max-w-md mx-auto lg:mx-0 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_daas-global/artifacts/tzekgvaf_IMG_0193.jpeg"
                  alt="Saad Abdullah Afridi - Managing Director"
                  className="w-full h-full object-cover object-center"
                  data-testid="md-profile-image"
                />
              </div>
            </ScrollReveal>

            {/* Profile Content */}
            <ScrollReveal delay={150}>
              <div>
              <p className="text-[var(--secondary)] text-xs font-bold uppercase tracking-[0.2em] mb-4">
                Founder & Managing Director
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--primary)] tracking-tight mb-4">
                Saad Abdullah Afridi
              </h2>
              <p className="text-[var(--text-secondary)] mb-6">
                Managing Director, DAAS Global | Director, DAAS Limited
              </p>
              <div className="gold-accent-line mb-6" />
              
              {/* Qualifications */}
              <div className="mb-6">
                <h4 className="text-sm font-bold uppercase tracking-wider text-[var(--primary)] mb-4">
                  Academic Qualifications
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-[var(--text-secondary)]">
                    <GraduationCap size={18} className="text-[var(--secondary)] flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-medium text-[var(--primary)]">BBA in Business Analytics</span>
                      <p className="text-sm">Hult International Business School, Cambridge, MA, USA</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-[var(--text-secondary)]">
                    <GraduationCap size={18} className="text-[var(--secondary)] flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-medium text-[var(--primary)]">Foundation in Astrophysics</span>
                      <p className="text-sm">Hertfordshire International College, Hatfield, UK</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-[var(--text-secondary)]">
                    <GraduationCap size={18} className="text-[var(--secondary)] flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-medium text-[var(--primary)]">Foundation in Physics</span>
                      <p className="text-sm">Cambridge Seminars College, Cambridge, UK</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="flex items-center gap-3 text-[var(--text-secondary)]">
                <Globe size={18} className="text-[var(--secondary)]" />
                <span>Remote strategic leadership</span>
              </div>
            </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Governance Model */}
      <section className="section-padding bg-white" data-testid="governance-model-section">
        <div className="container-main">
          <div className="max-w-2xl mb-12">
            <p className="text-[var(--secondary)] text-xs font-bold uppercase tracking-[0.2em] mb-4">
              Governance
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--primary)] tracking-tight mb-6">
              Structured for Accountability
            </h2>
            <div className="gold-accent-line" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {governanceFeatures.map((feature, index) => (
              <div
                key={feature.title}
                className="p-6 bg-[var(--background)] opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`governance-feature-${index}`}
              >
                <feature.icon size={24} strokeWidth={1.5} className="text-[var(--secondary)] mb-4" />
                <h3 className="text-base font-bold text-[var(--primary)] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOP Section */}
      <section className="governance-section section-padding" data-testid="sop-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[var(--secondary)] text-xs font-bold uppercase tracking-[0.2em] mb-4">
                Standard Operating Procedures
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-6">
                Process-Driven Excellence
              </h2>
              <div className="gold-accent-line mb-8" />
              <p className="text-white/80 text-lg leading-relaxed">
                Every aspect of our operations follows documented procedures ensuring 
                consistency, quality, and accountability across all services.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 p-8">
              <ul className="space-y-4">
                {['Student counseling protocols', 'Document verification procedures', 'Application submission workflows', 'Quality assurance checkpoints'].map((area, index) => (
                  <li key={index} className="flex items-center gap-4 text-white/80">
                    <CheckCircle2 size={18} className="text-[var(--secondary)] flex-shrink-0" />
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
